// Standalone, branded full-screen shell for the auth flow (login + OAuth callback).
// Deliberately renders NO app chrome (no sidebar, no legacy black MUI AppBar) so the
// sign-in experience is clean. Styling is self-contained here (inline + a scoped
// keyframe) so it doesn't depend on — or contend with — the dashboard component CSS.
import type { ReactNode } from "react";
import "../design/tokens.css";

const SPIN_KEYFRAMES = "@keyframes ab-auth-spin{to{transform:rotate(360deg)}}";

/** Teal ring spinner sized for the auth screen. */
export function AuthSpinner({ size = 26 }: { size?: number }) {
  return (
    <span
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        border: "2.5px solid rgba(255,255,255,0.16)",
        borderTopColor: "var(--kt-logo-bright, #1fb8ab)",
        display: "inline-block",
        animation: "ab-auth-spin 0.7s linear infinite",
      }}
    />
  );
}

/** The Aeronet Brain mark (4-dot cluster + wordmark + BRAIN tag), matching the top bar. */
function AuthLogo() {
  return (
    <span className="kt-logo" style={{ cursor: "default", pointerEvents: "none", transform: "scale(1.15)" }}>
      <span className="kt-logo-dots" aria-hidden="true"><span /><span /><span /><span /></span>
      <span className="kt-wordmark"><span className="kt-wm-aero">aero</span><span className="kt-wm-net">net</span></span>
      <span className="kt-brain-tag">brain</span>
    </span>
  );
}

/** Centered, dark, branded screen for sign-in / callback states. */
export function AuthScreen({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 36,
        padding: 24,
        background: "var(--kt-topbar-bg, #0e1214)",
        color: "#e7ebee",
        fontFamily: "var(--font-sans, system-ui)",
      }}
    >
      <style>{SPIN_KEYFRAMES}</style>
      <AuthLogo />
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 18,
          textAlign: "center",
          maxWidth: 380,
          width: "100%",
        }}
      >
        {children}
      </div>
    </div>
  );
}

/** Primary teal button for the auth screen (matches the app's primary action). */
export function AuthButton({ children, onClick }: { children: ReactNode; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        background: "var(--kt-logo-bright, #1fb8ab)",
        color: "#06231f",
        border: "none",
        borderRadius: 8,
        padding: "11px 22px",
        fontSize: 14,
        fontWeight: 600,
        fontFamily: "var(--font-sans, system-ui)",
        cursor: "pointer",
      }}
    >
      {children}
    </button>
  );
}
